import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { ContentHead, Page } from '../../lib/components';
import { ContentBox } from '../../lib/components/compound/pages/contentBox';
import { useStore } from '../../hooks/useStore';
import { Props as BenefitsOverlayProps } from '../../lib/components/compound/overlays/benfitsOverlay';
import { useTranslation } from 'react-i18next';
import { AddEmployeeButtonWithDropdown } from './components/dropdownButton';
import { EmployeeTable } from '../../lib/components/tables/employee/employee';
import { TooltipComponent } from '../../lib/components/atomic/feedback';
import { useCompanySettings, useHrisSetupStatus } from '../../queries/company';
import { StatusBadge } from '../../lib/components/atomic/dataDisplay/statusBadge';

export type BenefitsOverlayArgs = Omit<BenefitsOverlayProps, 'open'>;

const SYNC_STATUS_MODE: Record<string, 'error' | 'info' | 'success' | 'warning'> = {
  PENDING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'error',
  INCOMPLETE: 'warning'
};

const POLL_INTERVAL = 5000;

const EmployeesOverview: FC = () => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';

  const { data: companySettings } = useCompanySettings(companyId, {
    enabled: !!companyId,
    refetchInterval: (query: {
      state: { data?: { hris_last_sync_state?: string | null; hris_integration_status?: string } };
    }) => {
      const data = query.state.data;
      return data?.hris_last_sync_state === 'PENDING' || data?.hris_integration_status === 'CREATED'
        ? POLL_INTERVAL
        : false;
    }
  });

  const hrisStatus = companySettings?.hris_integration_status ?? '';
  const syncState =
    typeof companySettings?.hris_last_sync_state === 'string'
      ? companySettings.hris_last_sync_state
      : null;
  const syncDate = companySettings?.hris_last_sync_at;

  const { data: setupStatusData } = useHrisSetupStatus(companyId, {
    enabled: !!companyId && hrisStatus === 'CREATED'
  });
  const hrisSetupStatus = setupStatusData?.setup_status ?? null;

  const getEffectiveSyncState = () => {
    if (hrisStatus !== 'CREATED') return syncState;
    if (syncState === 'PENDING') return 'PENDING';
    if (hrisSetupStatus === 'INCOMPLETE') return 'INCOMPLETE';
    return 'PENDING';
  };
  const effectiveSyncState = getEffectiveSyncState();
  const showBadge =
    !['NONE', 'PAUSED'].includes(hrisStatus) &&
    effectiveSyncState &&
    effectiveSyncState in SYNC_STATUS_MODE;

  return (
    <Page>
      <ContentBox
        paddingBottom="0"
        headComponent={
          <ContentHead
            headline={t('common:EMPLOYEES')}
            subtitle={t('common:OVERVIEW_INFO')}
            renderActions={() => (
              <AddEmployeeButtonWithDropdown
                hrisStatus={hrisStatus}
                hrisSetupStatus={hrisSetupStatus}
              />
            )}
            showActions={true}
            renderAfterHeadline={() =>
              showBadge ? (
                <TooltipComponent
                  arrow
                  placement="bottom"
                  title={
                    <div className="text-white space-y-1">
                      <div className="text-sm/2">{t(`hris:${effectiveSyncState}_TOOLTIP`)}</div>
                      {syncDate && (
                        <div className="text-xs">
                          {t('hris:LAST_SYNC') +
                            ': ' +
                            new Date(syncDate).toLocaleString('de-DE', {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              timeZone: 'Europe/Berlin'
                            })}
                        </div>
                      )}
                    </div>
                  }
                >
                  <div>
                    <StatusBadge
                      label={t(`hris:${effectiveSyncState}`)}
                      mode={SYNC_STATUS_MODE[effectiveSyncState!]}
                    />
                  </div>
                </TooltipComponent>
              ) : (
                <></>
              )
            }
          />
        }
      >
        <EmployeeTable />
      </ContentBox>
    </Page>
  );
};

export default observer(EmployeesOverview);
